import { cn } from '@/lib/cn';
import type { DirectoryAttribute } from '@innermate/shared';

/** 이름의 이니셜 (아바타). */
function initials(name: string): string {
  const t = name.trim();
  if (!t) return '?';
  const parts = t.split(/\s+/);
  if (parts.length === 1) return t.slice(0, 1).toUpperCase();
  return ((parts[0]?.[0] ?? '?') + (parts[parts.length - 1]?.[0] ?? '?')).toUpperCase();
}

interface Props {
  displayName: string;
  email: string;
  attributes?: DirectoryAttribute[];
  /** 우측 강조 라벨 등. */
  trailing?: React.ReactNode;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * 사용자 1명을 일관된 형식으로 표시 — 아바타 + 이름 + 이메일 + 노출 허용 속성 칩.
 * 동명이인 구분을 위해 부서/직급 등 속성을 함께 보여준다 (관리자가 디렉터리 노출 허용한 것만).
 */
export function UserDisplay({
  displayName,
  email,
  attributes = [],
  trailing,
  size = 'md',
  className,
}: Props) {
  return (
    <div className={cn('flex items-center gap-3 min-w-0', className)}>
      <span
        className={cn(
          'shrink-0 rounded-full bg-accent/15 text-accent grid place-items-center font-semibold',
          size === 'sm' ? 'size-7 text-[11px]' : 'size-9 text-xs',
        )}
      >
        {initials(displayName)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-medium truncate">{displayName}</span>
          {attributes.map((a) => (
            <span
              key={`${a.label}:${a.value}`}
              className="inline-flex items-center rounded-full bg-surface-muted px-1.5 py-0.5 text-[10px] text-foreground-muted"
              title={a.label}
            >
              {a.value}
            </span>
          ))}
        </div>
        <div className="text-xs text-foreground-muted truncate">{email}</div>
      </div>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </div>
  );
}
